import { subscribeToOption } from "../../Utilities/Platform/Options/Options";
import BaseAgent, { generateWorkerURL } from "../Bases/BaseAgent";
import {
    SearchEvents,
    type SearchIndexes,
    SearchIndexGroups,
    type SearchSetupMessage,
} from "./SearchEvents";

export class SearchAgent extends BaseAgent {
    constructor() {
        super(generateWorkerURL("searchworker"), "Shopfront Search");

        this.startWorker();
        this.setup();
    }

    private setup(): void {
        const productSearchLevel = subscribeToOption("search_products_memory_level", (_, newValue) => {
            this.sendMessage(SearchEvents.CHANGE_SEARCH_LEVEL, {
                indexGroup: SearchIndexGroups.PRODUCTS,
                level     : newValue,
            });
        });

        const customerSearchLevel = subscribeToOption("search_customers_memory_level", (_, newValue) => {
            this.sendMessage(SearchEvents.CHANGE_SEARCH_LEVEL, {
                indexGroup: SearchIndexGroups.CUSTOMERS,
                level     : newValue,
            });
        });

        const searchSaveLocation = subscribeToOption(
            "search_save_location",
            (_, newValue) => {
                this.sendMessage(SearchEvents.CHANGE_SAVE_LOCATION, newValue);
            }
        );

        this.sendMessage(SearchEvents.SETUP, {
            searchLevels: {
                products : productSearchLevel,
                customers: customerSearchLevel,
            },
            saveLocation: searchSaveLocation,
        } as SearchSetupMessage["message"]);
    }

    protected handleMessage(_data: unknown): void {
        // Only sends callbacks, does not send messages originating from itself
        return;
    }

    public emptyIndex = (index: SearchIndexes): void => {
        this.sendMessage(SearchEvents.CLEAR, index);
    };

    public updateIndex = (index: SearchIndexes, id: string, values?: string | Array<string>): void => {
        this.sendMessage(SearchEvents.UPDATE, {
            index,
            id,
            values,
        });
    };

    public search = (index: SearchIndexes, needle: string): Promise<Array<string>> => {
        return new Promise(res => {
            this.sendMessage(SearchEvents.SEARCH, {
                index,
                needle,
            }, res);
        });
    };

    // Probably best not to use this method directly, instead use the SearchCommunicator
    public bulk = async <TMethod extends Omit<(keyof SearchAgent), "bulk">>(
        method: TMethod,
        args: Array<unknown>
    ): Promise<void> => {
        return new Promise(res => {
            this.sendMessage(SearchEvents.BULK, {
                method,
                args,
            }, res);
        });
    };
}
